<template>
  <el-row :gutter="20">
    <el-col
      v-if="configObject.finalCustomer && customerId"
      :span="12"
    >
      <FormItem :label="$t('analysesStatusManagement.edit.customerHeaderData')">
        <el-switch
          v-model="customerHeaderData"
          :active-text="
            $t('analysesStatusManagement.edit.customerHeaderDataYes')
          "
          :inactive-text="
            $t('analysesStatusManagement.edit.customerHeaderDataNo')
          "
          @change="resetAddress"
        />
      </FormItem>
    </el-col>
    <el-col
      v-if="customerHeaderData"
      :span="12"
    >
      <FormItem :label="$t('analysesStatusManagement.edit.customerAddress')">
        <el-select
          :value="localAddressId"
          filterable
          :filter-method="filterAddresses"
          style="width: 100%"
          @change="selectAddress"
          @visible-change="resetAddressQuery"
        >
          <el-option
            :value="null"
            :label="$t('analysesStatusManagement.edit.address.addNew')"
          />
          <el-option
            v-for="item in filteredAddresses"
            :key="item.id"
            :value="item.id"
            :label="`${item.name} - ${item.address}`"
          />
        </el-select>
      </FormItem>
    </el-col>
    <el-col
      :span="8"
      v-if="customerHeaderData"
    >
      <FormItem :label="$t('analysesStatusManagement.edit.address.name')">
        <el-input
          v-model="address.name"
          :maxlength="100"
          @change="update"
          :disabled="configObject.addressId !== null"
        />
      </FormItem>
    </el-col>
    <el-col
      :span="8"
      v-if="customerHeaderData"
    >
      <FormItem :label="$t('analysesStatusManagement.edit.address.address')">
        <el-input
          v-model="address.address"
          :maxlength="100"
          @change="update"
          :disabled="configObject.addressId !== null"
        />
      </FormItem>
    </el-col>
    <el-col
      :span="8"
      v-if="customerHeaderData"
    >
      <FormItem :label="$t('analysesStatusManagement.edit.address.city')">
        <el-input
          v-model="address.city"
          :maxlength="30"
          @change="update"
          :disabled="configObject.addressId !== null"
        />
      </FormItem>
    </el-col>
    <el-col
      :span="8"
      v-if="customerHeaderData"
    >
      <FormItem :label="$t('analysesStatusManagement.edit.address.zip')">
        <el-input
          v-model="address.zip"
          :maxlength="6"
          @change="update"
          :disabled="configObject.addressId !== null"
        />
      </FormItem>
    </el-col>
    <el-col
      :span="8"
      v-if="customerHeaderData"
    >
      <FormItem
        rules="required"
        :label="$t('analysesStatusManagement.edit.address.countryCode')"
      >
        <CountryCodeSelect
          v-model="address.countryCode"
          @change="update"
        />
      </FormItem>
    </el-col>
    <el-col
      :span="8"
      v-if="customerHeaderData"
    >
      <FormItem
        :rules="provinceRules"
        :label="$t('analysesStatusManagement.edit.address.province')"
      >
        <el-input
          v-model="address.province"
          :maxlength="isItaly ? 2 : 30"
          @change="update"
        />
      </FormItem>
    </el-col>
    <el-col
      :span="8"
      v-if="customerHeaderData"
    >
      <FormItem
        :label="$t('analysesStatusManagement.edit.address.phoneNumber')"
      >
        <el-input
          v-model="address.phoneNumber"
          :maxlength="14"
          @change="update"
          :disabled="configObject.addressId !== null"
        />
      </FormItem>
    </el-col>
    <el-col
      v-if="customerHeaderData && hasCountryChanges"
      :span="24"
    >
      <el-alert
        class="shared-address-notice"
        type="info"
        :closable="false"
        show-icon
        :title="$t('analysesStatusManagement.edit.address.sharedAddressNotice')"
      />
    </el-col>
  </el-row>
</template>

<script>
  import { customersApi } from '@/api';
  import FormItem from '@/components/FormItem.vue';
  import CountryCodeSelect from '@/components/CountryCodeSelect.vue';

  const ITALY_COUNTRY_CODE = 'IT';
  const COUNTRY_FIELDS = ['countryCode', 'province'];
  const ADDRESS_SEARCH_FIELDS = ['name', 'address', 'city'];

  const defaultAddressData = {
    id: null,
    name: null,
    address: null,
    city: null,
    phoneNumber: null,
    province: null,
    zip: null,
    countryCode: null,
  };

  export default {
    name: 'AddressForm',
    components: {
      FormItem,
      CountryCodeSelect,
    },
    props: {
      configObject: {
        type: Object,
        default: () => ({
          finalCustomer: null,
          addressId: null,
          customerId: null,
          countryPatch: null,
        }),
        required: true,
      },
    },
    computed: {
      localAddressId() {
        return this.configObject.addressId;
      },
      isItaly() {
        return this.address.countryCode === ITALY_COUNTRY_CODE;
      },
      provinceRules() {
        return this.isItaly
          ? { required: true, regex: /^[A-Za-z]{2}$/ }
          : { max: 30 };
      },
      countryPatch() {
        if (!this.address.id) return {};
        return Object.fromEntries(
          COUNTRY_FIELDS.map((key) => [key, this.address[key] || null]).filter(
            ([key, value]) => value !== this.loadedCountryFields[key]
          )
        );
      },
      filteredAddresses() {
        const query = this.addressQuery.trim().toLowerCase();
        if (!query) return this.addresses;
        return this.addresses.filter((item) =>
          ADDRESS_SEARCH_FIELDS.some((key) =>
            String(item[key] ?? '')
              .toLowerCase()
              .includes(query)
          )
        );
      },
      hasCountryChanges() {
        return Object.keys(this.countryPatch).length > 0;
      },
    },
    data() {
      return {
        customerHeaderData: false,
        addresses: [],
        addressQuery: '',
        customerId: this.configObject.customerId,
        loading: false,
        address: {},
        loadedCountryFields: { countryCode: null, province: null },
        finalCustomer: null,
      };
    },
    methods: {
      update() {
        this.$emit(
          'update',
          {
            ...this.address,
            customerHeaderData: this.customerHeaderData,
          },
          this.countryPatch
        );
      },
      filterAddresses(query) {
        this.addressQuery = query;
      },
      resetAddressQuery(visible) {
        if (visible) this.addressQuery = '';
      },
      snapshotCountryFields(address) {
        this.loadedCountryFields = {
          countryCode: address.countryCode || null,
          province: address.province || null,
        };
      },
      selectAddress(newAddressId) {
        if (newAddressId === null) {
          this.address = { ...defaultAddressData };
        } else {
          const selectedAddress = this.addresses.find(
            (a) => a.id === newAddressId
          );
          const {
            name,
            address,
            city,
            phoneNumber,
            province,
            zip,
            countryCode,
          } = selectedAddress;
          this.address = {
            id: newAddressId,
            name,
            address,
            city,
            phoneNumber,
            province,
            zip,
            countryCode,
          };
        }
        this.snapshotCountryFields(this.address);
        this.update();
      },
      resetAddress() {
        this.address = { ...defaultAddressData };
        this.update();
      },
      async fetchData() {
        try {
          if (this.configObject.addressId && this.configObject.finalCustomer) {
            this.customerHeaderData = true;
            this.addresses = await this.getCustomerAddresses(this.customerId);
            const address = await customersApi.getCustomerAddress(
              this.configObject.addressId
            );
            this.snapshotCountryFields(address.data);
            this.address = {
              ...address.data,
              ...this.configObject.countryPatch,
            };
            this.customerId = address.data.customerId;
            this.finalCustomer = this.configObject.finalCustomer;
          }
          if (this.configObject.customerId && !this.configObject.addressId) {
            this.resetAddress();
            this.customerHeaderData = false;
            this.finalCustomer = this.configObject.finalCustomer ?? null;
            this.addresses = await this.getCustomerAddresses(this.customerId);
          }
        } catch (error) {
          this.$notify({
            showClose: true,
            type: 'error',
            message: error.response.data.error.message,
          });
        }
      },
      async getCustomerAddresses(customerId) {
        const addresses = await customersApi.getCustomerAddresses(customerId);
        return addresses.data;
      },
    },
    async created() {
      await this.fetchData();
    },
    async beforeUpdate() {
      if (this.customerId !== this.configObject.customerId) {
        await this.fetchData();
      }
    },
  };
</script>

<style lang="scss" scoped>
  .shared-address-notice {
    margin-bottom: 22px;
  }
</style>
